import { useCallback, useEffect, useMemo, useState } from 'react';
import { myMeetAvailability, type MeetWindow, type MeetAvailabilityEntry } from '@/lib/api/calendlyClient';
import { toastError, toastSuccess } from '@/lib/toast';

const inputCls =
  'h-10 w-full rounded-lg border border-gray-300 px-3 text-[13px] text-gray-800 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white';

type Meet = MeetWindow & { categoryExpertId: string; availability: MeetAvailabilityEntry[] };

const WEEKDAY_FMT = new Intl.DateTimeFormat('es-CO', { weekday: 'short' });
const DAY_FMT = new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'short' });

// Días calendario específicos dentro de [startDate, endDate], ambos inclusive,
// como strings "YYYY-MM-DD" en UTC — para que la grilla no dependa de la
// zona horaria del navegador de quien la declara.
function daysInRange(startDate: string, endDate: string): string[] {
  const days: string[] = [];
  const cursor = new Date(startDate.slice(0, 10) + 'T00:00:00Z');
  const end = new Date(endDate.slice(0, 10) + 'T00:00:00Z');
  while (cursor <= end) {
    days.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return days;
}

// cccd-event-scoped-experts-and-availability: el propio experto declara,
// como en Calendly, días concretos dentro del rango del meet y las horas por
// día — nunca un input de fecha libre. Puramente informativo para que el
// admin coordine cobertura; NO afecta lo que un estudiante puede reservar en
// Calendly, y nadie más que el experto puede declarar/editar esto.
export default function MeetAvailabilityPanel() {
  const [meets, setMeets] = useState<Meet[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<Record<string, string>>({});
  const [hourDrafts, setHourDrafts] = useState<Record<string, { startTime: string; endTime: string }>>({});
  const [savingMeetId, setSavingMeetId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { meets: data } = await myMeetAvailability.listMeets();
      setMeets(data);
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'No se pudieron cargar los meets.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  function hourDraftFor(meetId: string) {
    return hourDrafts[meetId] ?? { startTime: '', endTime: '' };
  }

  async function onAdd(meet: Meet) {
    const day = selectedDay[meet.id];
    const hours = hourDraftFor(meet.id);
    if (!day || !hours.startTime || !hours.endTime) return;
    setSavingMeetId(meet.id);
    try {
      await myMeetAvailability.declare({
        meetWindowId: meet.id,
        date: new Date(day + 'T00:00:00Z').toISOString(),
        startTime: hours.startTime,
        endTime: hours.endTime,
      });
      setHourDrafts((prev) => ({ ...prev, [meet.id]: { startTime: '', endTime: '' } }));
      await load();
      toastSuccess('Disponibilidad declarada.');
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'No se pudo declarar la disponibilidad.');
    } finally {
      setSavingMeetId(null);
    }
  }

  async function onRemove(entryId: string) {
    setRemovingId(entryId);
    try {
      await myMeetAvailability.remove(entryId);
      await load();
    } catch (err: any) {
      toastError(err?.response?.data?.error ?? 'No se pudo quitar la disponibilidad.');
    } finally {
      setRemovingId(null);
    }
  }

  if (loading) return null;
  if (meets.length === 0) return null;

  return (
    <div className="mt-6 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Disponibilidad en meets de eventos</h3>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Solo para coordinación interna del admin — no afecta lo que un estudiante puede reservar en tu Calendly.
        </p>
      </div>

      {meets.map((meet) => (
        <MeetCard
          key={meet.id}
          meet={meet}
          selectedDay={selectedDay[meet.id] ?? ''}
          onSelectDay={(day) => setSelectedDay((prev) => ({ ...prev, [meet.id]: day }))}
          hourDraft={hourDraftFor(meet.id)}
          onChangeHours={(hours) => setHourDrafts((prev) => ({ ...prev, [meet.id]: hours }))}
          saving={savingMeetId === meet.id}
          removingId={removingId}
          onAdd={() => onAdd(meet)}
          onRemove={onRemove}
        />
      ))}
    </div>
  );
}

function MeetCard({
  meet,
  selectedDay,
  onSelectDay,
  hourDraft,
  onChangeHours,
  saving,
  removingId,
  onAdd,
  onRemove,
}: {
  meet: Meet;
  selectedDay: string;
  onSelectDay: (day: string) => void;
  hourDraft: { startTime: string; endTime: string };
  onChangeHours: (hours: { startTime: string; endTime: string }) => void;
  saving: boolean;
  removingId: string | null;
  onAdd: () => void;
  onRemove: (entryId: string) => void;
}) {
  const days = useMemo(() => daysInRange(meet.startDate, meet.endDate), [meet.startDate, meet.endDate]);
  const declaredByDay = useMemo(() => {
    const map: Record<string, MeetAvailabilityEntry[]> = {};
    for (const entry of meet.availability) {
      const day = entry.date.slice(0, 10);
      (map[day] ??= []).push(entry);
    }
    return map;
  }, [meet.availability]);

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-white/[0.03]">
      <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{meet.label}</p>
      <p className="text-xs text-gray-500 dark:text-gray-400">{meet.startDate.slice(0, 10)} – {meet.endDate.slice(0, 10)}</p>

      <p className="mb-1.5 mt-3 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Elige un día del rango</p>
      <div className="flex flex-wrap gap-1.5">
        {days.map((day) => {
          const hasDeclared = Boolean(declaredByDay[day]?.length);
          const isSelected = day === selectedDay;
          const d = new Date(day + 'T00:00:00Z');
          return (
            <button
              key={day}
              type="button"
              onClick={() => onSelectDay(day)}
              className={`flex flex-col items-center rounded-lg border px-2.5 py-1.5 text-center transition-colors ${
                isSelected
                  ? 'border-brand-500 bg-brand-50 text-brand-600 dark:bg-brand-500/10'
                  : hasDeclared
                    ? 'border-success-300 bg-success-50 text-success-700 dark:border-success-500/40 dark:bg-success-500/10 dark:text-success-400'
                    : 'border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-white/[0.04]'
              }`}
            >
              <span className="text-[11px] uppercase">{WEEKDAY_FMT.format(d)}</span>
              <span className="text-[13px] font-medium">{DAY_FMT.format(d)}</span>
            </button>
          );
        })}
      </div>

      {selectedDay && (
        <div className="mt-3 rounded-xl bg-gray-25 p-3 dark:bg-white/[0.02]">
          {declaredByDay[selectedDay]?.length ? (
            <ul className="mb-3 space-y-1.5">
              {declaredByDay[selectedDay].map((entry) => (
                <li key={entry.id} className="flex items-center justify-between rounded-lg bg-white px-3 py-1.5 text-[13px] dark:bg-gray-900">
                  <span className="text-gray-600 dark:text-gray-400">{entry.startTime}–{entry.endTime}</span>
                  <button
                    type="button"
                    onClick={() => onRemove(entry.id)}
                    disabled={removingId === entry.id}
                    className="text-xs font-medium text-error-600 hover:underline disabled:opacity-50"
                  >
                    Quitar
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">Sin horas declaradas este día todavía.</p>
          )}

          <div className="grid grid-cols-2 gap-2">
            <input
              type="time"
              value={hourDraft.startTime}
              onChange={(e) => onChangeHours({ ...hourDraft, startTime: e.target.value })}
              className={inputCls}
            />
            <input
              type="time"
              value={hourDraft.endTime}
              onChange={(e) => onChangeHours({ ...hourDraft, endTime: e.target.value })}
              className={inputCls}
            />
          </div>
          <button
            type="button"
            onClick={onAdd}
            disabled={!hourDraft.startTime || !hourDraft.endTime || saving}
            className="mt-2 h-9 whitespace-nowrap rounded-lg bg-brand-500 px-4 text-[13px] font-medium text-white hover:bg-brand-600 disabled:opacity-60"
          >
            {saving ? 'Guardando…' : '+ Agregar hora'}
          </button>
        </div>
      )}
    </div>
  );
}
